import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef
} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { setSidePanel, closeSidePanel } from 'reducers/sidePanel/actions';
import { toggleAccountMenuDropdown } from 'reducers/customer/actions';
import { default as HasRoleSubmenu } from 'components/Submenu';

import {
  RESOURCE_TYPES,
  SUBMENU_TYPES,
  WRITE_ACCESS_AUTH_ROLES,
  SIDEPANEL_STATES,
  defaultRowHeight,
  defaultItemsToDisplayInSingleRow,
  defaultSecondaryRowHeight
} from 'utils/appConstants';
import {
  POLL_CURVES,
  POLL_DEVICES,
  subscribe,
  unsubscribe
} from 'reducers/polling/actions';
import { FlexPage } from 'components/CommonStyles';
import { isExternallyManaged } from 'utils/format';
import Table from 'components/Table';

const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);
const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const Submenu = HasRoleSubmenu(WRITE_ACCESS_AUTH_ROLES);

const DevicesPage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const Devices = () => {
  const dispatch = useDispatch();
  const devices = useSelector(state => state.device.devices);
  const showAcctMenuDropdown = useSelector(
    state => state.customer.showAcctMenuDropdown
  );
  const [showSubmenuDropdown, setShowMenuDropDown] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const sidePanelIsOpen = useSelector(
    state => state.sidePanel.displaySidePanel
  );
  const devicesTableRef = useRef(null);

  const subName = 'Devices';

  useEffect(() => {
    dispatch(
      subscribe({
        subscriber: subName,
        pollTypes: [
          {
            type: POLL_DEVICES
          },
          {
            type: POLL_CURVES
          }
        ],
        frequency: pollRate
      })
    );
    setIsFetchingData(false);

    return () => {
      dispatch(unsubscribe(subName));
    };
  }, [dispatch]);

  // If the sidepanel closes, deselect any selected items on the table
  useEffect(() => {
    if (devicesTableRef.current?.api && !sidePanelIsOpen) {
      devicesTableRef.current.api.clearFocusedCell();
    }
  }, [sidePanelIsOpen]);

  const columnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'Device ID',
        field: 'deviceId',
        maxWidth: 350
      },
      {
        headerName: 'LFDI',
        field: 'lfdi'
      }
    ];
    if (externallyManagedEdevs) {
      columns.push({
        headerName: 'Remotely Managed',
        field: 'localDevice',
        cellRenderer: ({ value }) => isExternallyManaged(value),
        maxWidth: 120
      });
    }
    columns.push(
      {
        headerName: 'Registration Pin',
        field: 'registrationPin',
        maxWidth: 150
      },
      {
        headerName: 'Parent Topology Node',
        field: 'topoNodeIdList',
        maxWidth: 200,
        cellRenderer: ({ value }) => {
          if (!value) {
            return '';
          }
          const topoNodeDisplayList =
            value.length > defaultItemsToDisplayInSingleRow
              ? value
                  .sort()
                  .slice(0, defaultItemsToDisplayInSingleRow)
                  .concat(['...'])
              : value.sort();
          return (
            <ul>
              {topoNodeDisplayList.map(topoNodeId => (
                <li key={topoNodeId}>{topoNodeId}</li>
              ))}
            </ul>
          );
        }
      }
    );
    return columns;
  }, []);

  const onRowClicked = useCallback(
    row => {
      const { sfdi } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.DEVICE,
          state: SIDEPANEL_STATES.READ,
          sfdi
        })
      );
    },
    [dispatch]
  );

  const openCreatePanel = async (e, panelType) => {
    e.preventDefault();
    setShowMenuDropDown(false);
    await dispatch(closeSidePanel());
    dispatch(
      setSidePanel({
        entity: RESOURCE_TYPES[panelType.toUpperCase()],
        state: SIDEPANEL_STATES.CREATE
      })
    );
  };

  const toggleMenu = () => {
    if (showAcctMenuDropdown) {
      dispatch(toggleAccountMenuDropdown(false));
    }
    if (showSubmenuDropdown) {
      setShowMenuDropDown(false);
    }
  };

  const getRowHeight = ({ data }) => {
    const { topoNodeIdList } = data;

    if (topoNodeIdList) {
      if (topoNodeIdList.length > defaultItemsToDisplayInSingleRow) {
        return (
          defaultRowHeight +
          defaultSecondaryRowHeight * defaultItemsToDisplayInSingleRow
        );
      }
      return (
        defaultRowHeight +
        defaultSecondaryRowHeight * (topoNodeIdList.length - 1)
      );
    }
    return defaultRowHeight;
  };

  return (
    <FlexPage>
      <DevicesPage onClick={toggleMenu}>
        <Submenu
          dropdownIsShowing={showSubmenuDropdown}
          selectOption={openCreatePanel}
          toggleDropdownVisibility={isShowing => setShowMenuDropDown(isShowing)}
          submenuType={SUBMENU_TYPES.TOPOLOGY}
        />
        <FlexPage>
          <Table
            ref={devicesTableRef}
            onRowClicked={onRowClicked}
            columnDefs={columnDefs}
            rowData={devices}
            bigTitle="Current Devices"
            width="1000px"
            height="550px"
            getRowHeight={getRowHeight}
          />
        </FlexPage>
      </DevicesPage>
    </FlexPage>
  );
};

export default Devices;
